import { useCallback, useEffect, useState } from 'react';
import { pairTradingApi, PairReferencePnl } from '../services/pairTradingApi';

/**
 * The pair engine's OPEN-trigger breakdown, polled from
 * GET /pair/reference-pnl/:portfolio.
 *
 * Polls rather than streams because the value is a Redis snapshot the worker
 * rewrites on every futures tick — there is no push channel for it, and the read
 * is one GET. The interval is deliberately tighter than the positions poll: this
 * is the number that decides whether a pair opens, so an operator watching it
 * wants it to move.
 *
 * Soft-fails to null on any error; the caller falls back to its local preview.
 */
const POLL_MS = 5000;

export function usePairReferencePnl(portfolioId: string | null, enabled = true) {
    const [data, setData] = useState<PairReferencePnl | null>(null);

    const fetch = useCallback(async () => {
        if (!portfolioId) {
            setData(null);
            return;
        }
        try {
            setData(await pairTradingApi.getReferencePnl(portfolioId));
        } catch {
            setData(null);
        }
    }, [portfolioId]);

    useEffect(() => {
        if (!enabled || !portfolioId) return;
        void fetch();
        const timer = setInterval(() => void fetch(), POLL_MS);
        return () => clearInterval(timer);
    }, [enabled, portfolioId, fetch]);

    return { data, refresh: fetch };
}
